import { device as deviceUtils } from '@seek/ca-myprofile-utils';
import { metrics } from '@seek/metrics-js';
import type { RefObject } from 'react';

import { validateImage, ValidationError, SystemError } from '../validateImage';

interface Props {
  navigateToCropStep: () => void;
  openDialog: () => void;
  fileInputRef: RefObject<HTMLInputElement>;
  setOriginalFile: (file: File | null) => void;
  setValidationError: (value: ValidationError | SystemError | false) => void;
}

export const AddPhotoInput = ({
  navigateToCropStep,
  openDialog,
  fileInputRef,
  setOriginalFile,
  setValidationError,
}: Props) => {
  const onSafari = deviceUtils.isSafari();

  /**
   * iOS 26.2+ has a bug where the Braid Modal's FocusLock doesn't activate
   * properly after returning from the file picker, leaving the modal stuck
   * in OPENING state (invisible). This forces activation by focusing the
   * dialog heading and dispatching a window focus event.
   */
  const forceDialogActivation = () => {
    const dialog = document.getElementById('add-photo-dialog');
    if (!dialog) {
      return;
    }

    // Focus the heading (what Braid does in FocusLock's onActivation)
    const heading = dialog.querySelector(
      'h1, h2, h3, h4, h5, h6',
    ) as HTMLElement | null;
    if (heading) {
      if (!heading.hasAttribute('tabindex')) {
        heading.setAttribute('tabindex', '-1');
      }
      heading.focus();
    }

    // Trigger window focus event which FocusLock listens to
    window.dispatchEvent(new FocusEvent('focus'));
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (file) {
      metrics.count('avatar_photo_selected');
      // Reset so the next pick (including same file) fires change in all browsers.
      event.target.value = '';

      try {
        await validateImage(file);
        setOriginalFile(file);
        openDialog();
        navigateToCropStep();

        // Force dialog activation after render (iOS 26.2+ fix)
        requestAnimationFrame(() => {
          forceDialogActivation();
        });

        // Retry since the bug is intermittent
        setTimeout(forceDialogActivation, 100);
        setTimeout(forceDialogActivation, 300);
      } catch (error: unknown) {
        if (error instanceof ValidationError || error instanceof SystemError) {
          setValidationError(error);
        } else {
          const message =
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred';
          setValidationError(
            new SystemError(message, {
              fileType: file.type,
              fileSize: `${(file.size / 1024).toFixed(2)} KB`,
              file: file.name,
            }),
          );
        }
      }
    }
  };

  return (
    <input
      ref={fileInputRef}
      type="file"
      accept={`.jpg, .jpeg, .png ${onSafari ? ', .heic' : ''}`}
      onChange={onFileChange}
      aria-label="Avatar file input"
      title="Avatar file input"
      style={{ display: 'none' }}
      data-automation="avatar-file-input"
    />
  );
};
